import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toastr } from 'react-redux-toastr'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import type { CellMove, CommitValue } from '../lib/editing'
import { parseMoneyInput, toEditText } from '../lib/money'
import type { EditableColumn } from '../lib/permissions'

interface Props {
  column: EditableColumn
  /** The name or code text, or the stored decimal string of a money column. */
  initial: string
  locale: string
  onCommit: (value: CommitValue, move: CellMove) => void
  onCancel: () => void
}

/** Enter = save and go down, Tab / Shift+Tab = save and go to the next / previous editable cell, Escape = discard,
 * leaving the cell = save. Invalid text keeps the editor open on Enter / Tab; leaving the cell discards it with a toast.
 * Unmounting without a commit (the row scrolled out of the rendered window, the filter changed) counts as leaving. */
const CellEditor: React.FC<Props> = ({ column, initial, locale, onCommit, onCancel }) => {
  const { t } = useTranslation()
  const money = column === 'originalBudget' || column === 'estimate' || column === 'projectedFinalCost'
  const [text, setText] = useState(() => (money ? toEditText(initial, locale) : initial))
  const [invalid, setInvalid] = useState(false)
  const done = useRef(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const toValue = (raw: string): CommitValue | null => {
    if (column === 'originalBudget' || column === 'estimate' || column === 'projectedFinalCost') {
      const parsed = parseMoneyInput(raw)
      return parsed.ok ? { column, money: parsed.value } : null
    }
    const trimmed = raw.trim()
    if (column === 'name' && !trimmed) return null
    return { column, text: trimmed }
  }

  const commit = (move: CellMove) => {
    if (done.current) return
    const value = toValue(text)
    if (!value) { setInvalid(true); return }
    done.current = true
    onCommit(value, move)
  }

  const cancel = () => {
    done.current = true
    onCancel()
  }

  // Also runs from the unmount cleanup: reads the text through a ref and sets no state of its own.
  const textRef = useRef(text)
  textRef.current = text
  const leave = () => {
    if (done.current) return
    done.current = true
    const value = toValue(textRef.current)
    if (value) {
      onCommit(value, 'stay')
    } else {
      toastr.error(t('Common_error'), t('CostControl_edit_discarded'))
      onCancel()
    }
  }
  const leaveRef = useRef(leave)
  leaveRef.current = leave

  useEffect(() => {
    inputRef.current?.focus()
    inputRef.current?.select()
    return () => leaveRef.current()
  }, [])

  return (
    <Input
      ref={inputRef}
      value={text}
      aria-invalid={invalid || undefined}
      title={invalid ? t(money ? 'CostControl_error_bad_amount' : 'CostControl_error_name_required') : undefined}
      className={cn('h-7 w-full px-1 text-sm', money && 'text-right tabular-nums', invalid && 'border-destructive focus-visible:ring-destructive')}
      onChange={(e) => { setText(e.target.value); setInvalid(false) }}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        e.stopPropagation()
        if (e.key === 'Enter') { e.preventDefault(); commit('down') }
        else if (e.key === 'Tab') { e.preventDefault(); commit(e.shiftKey ? 'prev' : 'next') }
        else if (e.key === 'Escape') { e.preventDefault(); cancel() }
      }}
      onBlur={leave}
    />
  )
}
export default CellEditor
